import { useCallback } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router";
import { handlePlainLinkClick } from "@bastion/ui";
import { matchProductGroup, visibleProductTabs, type ProductGroupId, type ProductTopTab } from "../navigation";
import { useOrgRendering } from "../useOrgRendering";
import { withPhoneView } from "./phoneViews";
import { ProfileMenuButton } from "./ProfileMenuButton";
import styles from "./AppRail.module.css";

export interface AppRailProps {
  pathname: string;
  /**
   * True on a phone, where the rail is in the navigation drawer (AppLayout).
   * A product link then opens its page under the drawer and keeps the
   * drawer open, so the section list changes in place.
   */
  inDrawer?: boolean;
}

function isRailProductActive(tab: ProductTopTab, group: ProductGroupId): boolean {
  if (tab.id === "home") return group === "chat";
  return group === tab.id;
}

export function AppRail({ pathname, inDrawer = false }: AppRailProps) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const group = matchProductGroup(pathname);
  // Default open: the rail holds every tab until `/me` answers that this
  // organization is not Parametric. See `visibleProductTabs`.
  const me = useOrgRendering();
  const tabs = visibleProductTabs(me);

  const handleProductNav = useCallback(
    (path: string) => {
      navigate(path, inDrawer ? { replace: true } : undefined);
    },
    [inDrawer, navigate],
  );

  // In the drawer, the page of the product takes the entry of the drawer,
  // and a new entry opens the drawer over that page. The scrim, Esc and
  // Back then close the drawer and keep the new page. A link to the page
  // that is open does nothing.
  const openProductInDrawer = useCallback(
    (path: string) => {
      if (path === pathname + search) return;
      navigate(path, { replace: true });
      navigate(path, { state: withPhoneView(null, "sideNav") });
    },
    [navigate, pathname, search],
  );

  return (
    <nav className={styles.rail} aria-label="Primary">
      <div className={styles.railTop}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isRailProductActive(tab, group);
          return (
            <RouterLink
              key={tab.id}
              to={tab.defaultPath}
              onClick={
                inDrawer
                  ? (event) =>
                      handlePlainLinkClick(event, () => openProductInDrawer(tab.defaultPath))
                  : undefined
              }
              className={[
                styles.railButton,
                styles.railProduct,
                active ? styles.railButtonActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              title={tab.label}
              aria-label={tab.label}
              data-testid={`sidebar-item-${tab.id}`}
              aria-current={active ? "page" : undefined}
            >
              <Icon className={styles.railIcon} aria-hidden="true" />
              <span className={styles.railLabel} aria-hidden="true">
                {tab.label}
              </span>
            </RouterLink>
          );
        })}
      </div>
      <div className={styles.railBottom}>
        <ProfileMenuButton
          placement="top-start"
          triggerClassName={styles.railButton}
          onNavigate={handleProductNav}
        />
      </div>
    </nav>
  );
}
